import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, FolderKanban, Wallet, TrendingUp, ArrowUpRight, 
  ArrowDownRight, Clock, AlertCircle, CheckCircle, DollarSign
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { admin, token, stats, refreshStats } = useOutletContext();
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentActivity();
  }, [token]);

  const fetchRecentActivity = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard/recent-activity?token=${token}&limit=10`);
      setRecentActivity(response.data);
    } catch (error) {
      console.error('Failed to fetch activity');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Users',
      value: stats?.users?.total || 0,
      change: stats?.users?.new_this_week || 0,
      changeLabel: 'new this week',
      icon: Users,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Active Groups',
      value: stats?.groups?.active || 0,
      change: stats?.groups?.new_this_week || 0,
      changeLabel: 'new this week',
      icon: FolderKanban,
      color: 'from-violet-500 to-purple-500'
    },
    {
      title: 'Total Contributions',
      value: `₹${(stats?.contributions?.total_amount || 0).toLocaleString()}`,
      change: stats?.contributions?.total_verified || 0,
      changeLabel: 'verified',
      icon: Wallet,
      color: 'from-green-500 to-emerald-500'
    },
    {
      title: 'Total Expenses',
      value: `₹${(stats?.expenses?.total_amount || 0).toLocaleString()}`,
      change: stats?.expenses?.total_count || 0,
      changeLabel: 'expenses',
      icon: DollarSign,
      color: 'from-orange-500 to-amber-500'
    }
  ];

  const pendingItems = [
    {
      label: 'Pending Contributions',
      count: stats?.contributions?.pending || 0,
      icon: Clock,
      color: 'text-yellow-400'
    },
    {
      label: 'Pending Loan Requests',
      count: stats?.lending?.pending || 0,
      icon: AlertCircle,
      color: 'text-orange-400'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Message */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-6 border border-slate-700"
      >
        <h1 className="text-2xl font-bold text-white mb-2">
          Welcome back, {admin?.name || 'Admin'}! 👋
        </h1>
        <p className="text-slate-400">
          Here's what's happening with SharePay today.
        </p>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-slate-900 rounded-2xl p-5 border border-slate-800"
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <div className="flex items-center gap-1 text-green-400 text-sm">
                <ArrowUpRight className="w-4 h-4" />
                <span>{stat.change}</span>
              </div>
            </div>
            <h3 className="text-slate-400 text-sm mb-1">{stat.title}</h3>
            <p className="text-2xl font-bold text-white">{stat.value}</p>
            <p className="text-xs text-slate-500 mt-1">{stat.changeLabel}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pending Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-slate-900 rounded-2xl p-6 border border-slate-800"
        >
          <h2 className="text-lg font-bold text-white mb-4">Pending Actions</h2>
          <div className="space-y-3">
            {pendingItems.map((item) => (
              <div
                key={item.label}
                className="flex items-center justify-between p-4 rounded-xl bg-slate-800/50"
              >
                <div className="flex items-center gap-3">
                  <item.icon className={`w-5 h-5 ${item.color}`} />
                  <span className="text-slate-300">{item.label}</span>
                </div>
                <span className={`text-xl font-bold ${item.count > 0 ? item.color : 'text-slate-500'}`}>
                  {item.count}
                </span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="lg:col-span-2 bg-slate-900 rounded-2xl p-6 border border-slate-800"
        >
          <h2 className="text-lg font-bold text-white mb-4">Recent Activity</h2>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {loading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <p className="text-slate-400">Loading...</p>
              </div>
            ) : recentActivity.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                No recent activity
              </div>
            ) : (
              recentActivity.map((activity, index) => (
                <div
                  key={activity.id || index}
                  className="flex items-start gap-3 p-3 rounded-xl bg-slate-800/30"
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    activity.activity_type === 'expense_added' ? 'bg-orange-500/20 text-orange-400' :
                    activity.activity_type === 'settlement' ? 'bg-green-500/20 text-green-400' :
                    activity.activity_type === 'level_up' ? 'bg-purple-500/20 text-purple-400' :
                    'bg-blue-500/20 text-blue-400'
                  }`}>
                    {activity.activity_type === 'expense_added' ? <DollarSign className="w-4 h-4" /> :
                     activity.activity_type === 'settlement' ? <CheckCircle className="w-4 h-4" /> :
                     activity.activity_type === 'level_up' ? <TrendingUp className="w-4 h-4" /> :
                     <Users className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{activity.title}</p>
                    <p className="text-slate-400 text-xs truncate">{activity.description}</p>
                  </div>
                  <span className="text-xs text-slate-500 flex-shrink-0">
                    {new Date(activity.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))
            )}
          </div>
        </motion.div>
      </div>

      {/* Quick Stats Summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-gradient-to-r from-red-500/10 to-orange-500/10 rounded-2xl p-6 border border-orange-500/20"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-3xl font-bold text-white">{stats?.groups?.total || 0}</p>
            <p className="text-sm text-slate-400">Total Groups</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-white">{stats?.contributions?.total_verified || 0}</p>
            <p className="text-sm text-slate-400">Verified Contributions</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-white">{stats?.lending?.total_requests || 0}</p>
            <p className="text-sm text-slate-400">Lending Requests</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-white">{stats?.expenses?.total_count || 0}</p>
            <p className="text-sm text-slate-400">Total Expenses</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminDashboard;
